import { useState, useCallback, useRef } from 'react'
import { EditorContent } from './editorContent/types'
import { TrueNativeArticle } from './articleManagement/types'

interface UseTrueNativeEditorOptions {
	campaignId: string
	articleId?: string
	initialData?: TrueNativeArticle
	onSave?: (data: TrueNativeArticle) => void
	onCancel?: () => void
}

export const useTrueNativeEditor = ({
	campaignId,
	articleId,
	initialData,
	onSave,
	onCancel,
}: UseTrueNativeEditorOptions) => {
	// Article state management - editor state removed
	const [activeArticleTab, setActiveArticleTab] = useState<string>('article-1')
	const [title, setTitle] = useState(initialData?.title ?? 'Untitled Article')
	const [isDirty, setIsDirty] = useState(false)

	// Content reference for saving - managed by EditorProvider
	const contentRef = useRef<EditorContent>({
		wysiwyg: initialData?.content?.wysiwyg ?? '',
		html: initialData?.content?.html ?? '',
		css: initialData?.content?.css ?? '',
	})

	// Content update handler for EditorProvider integration
	const handleContentUpdate = useCallback((newContent: EditorContent) => {
		contentRef.current = newContent
		setIsDirty(true)
	}, [])

	// Article-specific handlers
	const handleTitleChange = useCallback((newTitle: string) => {
		setTitle(newTitle)
		setIsDirty(true)
	}, [])

	const handleArticleTabSelect = useCallback((tabKey: string) => {
		setActiveArticleTab(tabKey)
		// TODO: Later this will switch between different articles or create new ones
	}, [])

	const handleDirtyStateChange = useCallback((dirty: boolean) => {
		setIsDirty(dirty)
	}, [])

	const handleSave = useCallback(() => {
		const articleData: TrueNativeArticle = {
			id: articleId,
			campaignId,
			title,
			content: contentRef.current, // Get current content from ref
			metadata: {
				updatedAt: new Date().toISOString(),
			},
		}

		// TODO: Add validation logic here
		// TODO: Add API call logic here

		onSave?.(articleData)
		setIsDirty(false)
	}, [articleId, campaignId, title, onSave])

	const handleCancel = useCallback(() => {
		// TODO: Add unsaved changes check
		if (isDirty) {
			// TODO: Show confirmation dialog
			// eslint-disable-next-line no-console
			console.warn('Unsaved changes detected')
		}
		onCancel?.()
	}, [isDirty, onCancel])

	return {
		// Article state - editor state removed
		activeArticleTab,
		title,
		isDirty,

		// Initial content for editor context
		initialContent: {
			wysiwyg: initialData?.content?.wysiwyg ?? '',
			html: initialData?.content?.html ?? '',
			css: initialData?.content?.css ?? '',
		},

		// Article handlers
		handleTitleChange,
		handleArticleTabSelect,
		handleSave,
		handleCancel,

		// Editor integration handlers
		handleContentUpdate,
		handleDirtyStateChange,
	}
}
